import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { formatGBP, relDate } from "../lib/format";
import { MEDIA_TONES } from "../lib/format";
import { toast } from "sonner";
import { Download, Share2, Camera, PlayCircle, BarChart3, Trophy, Sparkles, Check } from "lucide-react";

export default function SponsorPack({ publicMode = false }) {
  const { id, token } = useParams();
  const [pack, setPack] = useState(null);

  useEffect(() => {
    (publicMode ? api.publicSponsorPack(token) : api.getSponsorPack(id)).then(setPack);
  }, [id, token, publicMode]);

  if (!pack) return <Card className="p-8 text-sm text-muted-foreground">Loading pack…</Card>;

  const p = pack.payload || {};
  const d = p.driver_snapshot || {};
  const ai = p.ai_summary || {};
  const fund = d.funding_need || {};
  const fundPct = fund.total_required ? Math.min(100, Math.round((fund.secured / fund.total_required) * 100)) : 0;

  const copyLink = () => {
    const u = `${window.location.origin}/share/sponsor/${pack.share_token}`;
    navigator.clipboard.writeText(u);
    toast.success("Shareable link copied");
  };
  const downloadJSON = () => {
    const blob = new Blob([JSON.stringify(pack, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${pack.id}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={publicMode ? "min-h-screen bg-background" : ""} data-testid={publicMode ? "public-sponsor-pack" : "sponsor-pack-page"}>
      {publicMode && (
        <header className="border-b border-border bg-white">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="font-semibold">ClubCierge · Sponsor pack</div>
            <span className="text-[10px] tracking-eyebrow font-mono-ui px-2 py-0.5 rounded border border-border">Read-only · {pack.id}</span>
          </div>
        </header>
      )}

      <div className={`${publicMode ? "max-w-4xl mx-auto px-6 py-10" : ""} space-y-6`}>
        {!publicMode && (
          <Link to={`/drivers/${d.id}`} className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-driver">
            ← Back to driver
          </Link>
        )}

        {/* Pack header */}
        <section className="relative overflow-hidden rounded-2xl border border-border bg-white">
          <div className="checker-stripe absolute top-0 right-0 w-64 h-64 pointer-events-none" />
          <div className="relative p-8 grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 flex items-start gap-5">
              <div className="relative shrink-0">
                <img src={d.avatar_url} alt="" className="h-24 w-24 rounded-lg object-cover bg-muted" />
                <span className="absolute -bottom-2 -right-2 h-9 min-w-9 px-1.5 rounded-md bg-[#F25E1D] text-white text-[14px] font-semibold grid place-items-center font-mono-ui">
                  #{d.race_number}
                </span>
              </div>
              <div className="min-w-0">
                <div className="tracking-eyebrow text-muted-foreground">{d.series}</div>
                <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold mt-1">{d.name}</h1>
                <div className="text-sm text-muted-foreground mt-1">Age {d.age} · {d.age_band} · {d.location}</div>
                {ai.headline && <p className="text-[15px] mt-3 max-w-2xl leading-relaxed">{ai.headline}</p>}
              </div>
            </div>
            <div className="lg:col-span-2 flex flex-col gap-3 items-start lg:items-end">
              <span className="inline-flex items-center gap-1 text-[10px] tracking-eyebrow font-mono-ui px-2 py-1 rounded border border-border signal-bg signal-text">
                <Sparkles className="h-3 w-3" /> AI-drafted · Officer-approved
              </span>
              <div className="text-right text-xs text-muted-foreground">
                Approved by <b className="text-foreground">{p.approved_by}</b><br />
                {p.approved_role} · {relDate(p.approved_at)}
              </div>
            </div>
          </div>
        </section>

        {/* Narrative */}
        <Card className="p-6 border-border race-card">
          <div className="tracking-eyebrow text-muted-foreground">Summary</div>
          <p className="mt-2 text-[16px] leading-relaxed">{ai.summary_paragraph}</p>

          <div className="grid sm:grid-cols-2 gap-5 mt-5">
            <div>
              <div className="tracking-eyebrow text-muted-foreground">Strengths</div>
              <ul className="mt-1.5 space-y-1 text-sm">
                {ai.strengths?.map((s, i) => <li key={i} className="flex gap-2"><Check className="h-3.5 w-3.5 mt-0.5 text-[var(--rag-green-text)]" /><span>{s}</span></li>)}
              </ul>
            </div>
            <div>
              <div className="tracking-eyebrow text-muted-foreground">Asks</div>
              <ul className="mt-1.5 space-y-1 text-sm">
                {ai.asks?.map((s, i) => <li key={i}>· {s}</li>)}
              </ul>
            </div>
          </div>
        </Card>

        {/* Performance highlights */}
        <Card className="p-6 border-border race-card">
          <div className="flex items-center justify-between mb-3">
            <div className="tracking-eyebrow text-muted-foreground">Performance highlights</div>
            <Trophy className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {(d.recent_results || []).slice(0, 4).map((r) => (
              <div key={r.event} className="flex items-center gap-3 rounded-lg border border-border p-3">
                <div className="w-10 h-10 grid place-items-center rounded-md border border-border font-mono-ui font-semibold">P{r.position}</div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{r.event}</div>
                  <div className="text-[11px] text-muted-foreground font-mono-ui">Best lap {r.lap_best} · {relDate(r.date)}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Funding */}
        <Card className="p-6 border-border race-card">
          <div className="grid lg:grid-cols-3 gap-5">
            <div>
              <div className="tracking-eyebrow text-muted-foreground">Support needed</div>
              <div className="text-3xl font-semibold mt-2">{formatGBP(fund.gap)}</div>
              <div className="text-xs text-muted-foreground font-mono-ui mt-1">{formatGBP(fund.secured)} of {formatGBP(fund.total_required)} · {fundPct}%</div>
              <div className="h-2 w-full rounded-full bg-secondary overflow-hidden mt-2">
                <div className="h-full rounded-full bg-[#F25E1D]" style={{ width: `${fundPct}%` }} />
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="tracking-eyebrow text-muted-foreground mb-2">Breakdown</div>
              <ul className="divide-y divide-border">
                {(fund.categories || []).map((c) => (
                  <li key={c.label} className="flex items-center justify-between py-2 text-sm">
                    <span>{c.label}</span>
                    <span className="font-mono-ui font-medium">{formatGBP(c.amount)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>

        {/* Media */}
        {d.media && d.media.length > 0 && (
          <Card className="p-6 border-border race-card">
            <div className="tracking-eyebrow text-muted-foreground mb-3">Media samples</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {d.media.map((m, i) => (
                <div key={i} className={`relative rounded-lg overflow-hidden bg-gradient-to-br ${MEDIA_TONES[m.tone] || MEDIA_TONES.paddock} aspect-[4/3]`}>
                  <div className="absolute inset-0 grid place-items-center">
                    {m.kind === "reel" ? <PlayCircle className="h-10 w-10 opacity-80" /> : m.kind === "stat" ? <BarChart3 className="h-10 w-10 opacity-80" /> : <Camera className="h-10 w-10 opacity-80" />}
                  </div>
                  <div className="absolute top-2 left-2 text-[10px] tracking-eyebrow font-mono-ui bg-black/30 backdrop-blur px-1.5 py-0.5 rounded">{m.badge}</div>
                  <div className="absolute bottom-2 left-2 right-2 text-[12px] font-medium drop-shadow">{m.title}</div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Endorsement */}
        {d.endorsements && d.endorsements[0] && (
          <Card className="p-6 border-border race-card bg-[#FAFAFB]">
            <div className="tracking-eyebrow text-muted-foreground">Coach endorsement</div>
            <p className="text-[16px] leading-relaxed mt-2">“{d.endorsements[0].quote}”</p>
            <div className="text-[11px] text-muted-foreground mt-2 font-mono-ui">— {d.endorsements[0].by}, {d.endorsements[0].role}</div>
          </Card>
        )}

        {/* Receipt footer */}
        <Card className="p-6 border-border race-card receipt-paper font-mono-ui text-xs space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="tracking-eyebrow text-muted-foreground">Pack ID</div>
              <div>{pack.id}</div>
            </div>
            <div>
              <div className="tracking-eyebrow text-muted-foreground">Hash</div>
              <div>{pack.hash}</div>
            </div>
          </div>
          <div className="dashed-divider" />
          <div style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} className="text-[11px] text-muted-foreground leading-relaxed">
            This sponsor pack is a club-approved snapshot for external sharing.
            AI helped structure the profile. A named club officer approved the content.
            Operational and safeguarding readiness were cleared at the time of approval.
          </div>
        </Card>

        {!publicMode && (
          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="outline" onClick={copyLink} data-testid="copy-share-link">
              <Share2 className="h-4 w-4 mr-1.5" /> Copy shareable link
            </Button>
            <Button onClick={downloadJSON} data-testid="download-pack-json">
              <Download className="h-4 w-4 mr-1.5" /> Download JSON
            </Button>
          </div>
        )}

        {publicMode && (
          <div className="text-center text-[11px] text-muted-foreground py-4">
            Shareable read-only sponsor pack · ClubCierge · AI recommends · Authorised humans decide · The club keeps the evidence
          </div>
        )}
      </div>
    </div>
  );
}
